import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  useFilterDialog as useCoreFilterDialog,
  type FilterDialogComponentProps,
  FlightSearchActions,
  getAirportFilter,
  getMaxPriceFilter,
  getOutboundDepartureTimeRange,
  getOutboundArrivalTimeRange,
  getReturnDepartureTimeRange,
  getReturnArrivalTimeRange,
  getStopsOption,
  getFareclassOptionFilter,
  getAirlineFilter,
  getFlightNumberFilter,
} from "@b2bportal/core-flights";
import type { ITimeRange } from "@b2bportal/core-types";
import { isEqual } from "lodash-es";
import { trackAppliedFilter, FilterType } from "@/utilities/tracking";

/**
 * Helper function to compare arrays
 */
const arraysEqual = (a: unknown[], b: unknown[]) =>
  a.length === b.length && a.every((val, index) => val === b[index]);

/**
 * Helper function to compare time ranges
 */
const timeRangesEqual = (a: ITimeRange, b: ITimeRange) =>
  a.min === b.min && a.max === b.max;

/**
 * Helper function to compare fareclass filters
 */
const fareclassFiltersEqual = (
  a: Record<string, boolean>,
  b: Record<string, boolean>
) => {
  const aKeys = Object.keys(a);
  const bKeys = Object.keys(b);
  if (aKeys.length !== bKeys.length) return false;
  return aKeys.every((key) => a[key] === b[key]);
};

/**
 * Helper function to compare flight number filters using deep equality
 */
const flightNumberFiltersEqual = (a: unknown[], b: unknown[]) => isEqual(a, b);

/**
 * RBC-specific filter dialog hook that extends core functionality
 * to add airport filter support and fix slider state management
 */
export const useFilterDialog = (props: FilterDialogComponentProps) => {
  const dispatch = useDispatch();
  const coreHook = useCoreFilterDialog(props);

  // Get filter state from selectors
  const airportFilterState = useSelector(getAirportFilter);
  const priceFilterState = useSelector(getMaxPriceFilter);
  const outboundDepartureTimeRangeState = useSelector(
    getOutboundDepartureTimeRange
  );
  const outboundArrivalTimeRangeState = useSelector(
    getOutboundArrivalTimeRange
  );
  const returnDepartureTimeRangeState = useSelector(
    getReturnDepartureTimeRange
  );
  const returnArrivalTimeRangeState = useSelector(getReturnArrivalTimeRange);

  // Get other filter states for comparison
  const stopsOptionState = useSelector(getStopsOption);
  const fareclassOptionState = useSelector(getFareclassOptionFilter);
  const airlineFilterState = useSelector(getAirlineFilter);
  const flightNumberFilterState = useSelector(getFlightNumberFilter);

  // Local state for filters
  const [airport, setAirport] = useState(airportFilterState);
  const [price, setPrice] = useState(priceFilterState);
  const [outboundDepartureTimeRange, setOutboundDepartureTimeRange] = useState(
    outboundDepartureTimeRangeState
  );
  const [outboundArrivalTimeRange, setOutboundArrivalTimeRange] = useState(
    outboundArrivalTimeRangeState
  );
  const [returnDepartureTimeRange, setReturnDepartureTimeRange] = useState(
    returnDepartureTimeRangeState
  );
  const [returnArrivalTimeRange, setReturnArrivalTimeRange] = useState(
    returnArrivalTimeRangeState
  );

  const handleAirportChange = (newAirports: string[]) => {
    setAirport(newAirports);
  };

  const handlePriceChange = ({ maxPrice }: { maxPrice: number }) => {
    setPrice(maxPrice);
  };

  const handleOutboundTimeRangeChange = (
    departureTimeRange: ITimeRange,
    arrivalTimeRange: ITimeRange
  ) => {
    setOutboundDepartureTimeRange(departureTimeRange);
    setOutboundArrivalTimeRange(arrivalTimeRange);
  };

  const handleReturnTimeRangeChange = (
    departureTimeRange: ITimeRange,
    arrivalTimeRange: ITimeRange
  ) => {
    setReturnDepartureTimeRange(departureTimeRange);
    setReturnArrivalTimeRange(arrivalTimeRange);
  };

  // Override updateValuesToMatchState to include all filters
  const updateValuesToMatchState = () => {
    coreHook.handlers.updateValuesToMatchState();
    setAirport(airportFilterState);
    setPrice(priceFilterState);
    setOutboundDepartureTimeRange(outboundDepartureTimeRangeState);
    setOutboundArrivalTimeRange(outboundArrivalTimeRangeState);
    setReturnDepartureTimeRange(returnDepartureTimeRangeState);
    setReturnArrivalTimeRange(returnArrivalTimeRangeState);
  };

  // Override resetAllFilters to include all filters
  const resetAllFilters = () => {
    coreHook.handlers.resetAllFilters();
    setAirport([]);
    setPrice(Number.MAX_SAFE_INTEGER);
    setOutboundDepartureTimeRange({ min: 0, max: 1440 });
    setOutboundArrivalTimeRange({ min: 0, max: 1440 });
    setReturnDepartureTimeRange({ min: 0, max: 1440 });
    setReturnArrivalTimeRange({ min: 0, max: 1440 });
  };

  // Override applyFilter to use local state values
  const applyFilter = () => {
    // Track which filters changed
    if (coreHook.context.stop !== stopsOptionState) {
      trackAppliedFilter(FilterType.STOPS);
    }

    if (price !== priceFilterState) {
      trackAppliedFilter(FilterType.PRICE);
    }

    if (
      !fareclassFiltersEqual(coreHook.context.fareClasses, fareclassOptionState)
    ) {
      trackAppliedFilter(FilterType.FARECLASS);
    }

    if (!arraysEqual(coreHook.context.airline, airlineFilterState)) {
      trackAppliedFilter(FilterType.AIRLINE);
    }

    if (!arraysEqual(airport, airportFilterState)) {
      trackAppliedFilter(FilterType.AIRPORT);
    }

    if (
      !flightNumberFiltersEqual(
        coreHook.context.flightNumber,
        flightNumberFilterState
      )
    ) {
      trackAppliedFilter(FilterType.FLIGHT_NUMBER);
    }

    // Check if any time range changed
    const outboundTimeChanged =
      !timeRangesEqual(
        outboundDepartureTimeRange,
        outboundDepartureTimeRangeState
      ) ||
      !timeRangesEqual(outboundArrivalTimeRange, outboundArrivalTimeRangeState);
    const returnTimeChanged =
      !timeRangesEqual(
        returnDepartureTimeRange,
        returnDepartureTimeRangeState
      ) ||
      !timeRangesEqual(returnArrivalTimeRange, returnArrivalTimeRangeState);

    if (outboundTimeChanged || returnTimeChanged) {
      trackAppliedFilter(FilterType.TIME);
    }

    dispatch(
      FlightSearchActions.setAllFilters({
        stopsOption: coreHook.context.stop,
        maxPriceFilter: price,
        fareclassOptionFilter: coreHook.context.fareClasses,
        airlineFilter: coreHook.context.airline,
        airportFilter: airport,
        flightNumberFilter: coreHook.context.flightNumber,
        outboundDepartureTimeRange,
        outboundArrivalTimeRange,
        returnDepartureTimeRange,
        returnArrivalTimeRange,
      })
    );
  };

  return {
    context: {
      ...coreHook.context,
      airport,
      priceState: price,
      outboundDepartureTimeRange,
      outboundArrivalTimeRange,
      returnDepartureTimeRange,
      returnArrivalTimeRange,
    },
    handlers: {
      ...coreHook.handlers,
      handleAirportChange,
      handlePriceChange,
      handleOutboundTimeRangeChange,
      handleReturnTimeRangeChange,
      updateValuesToMatchState,
      resetAllFilters,
      applyFilter,
    },
  };
};
